import { AuthService } from './authService';
import { extractApiError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

export interface SystemSetupDto {
  id: number;
  companyNo: string;
  companyName: string;
  orgNo?: string;
  maxLineItemQuantity?: number;
  smtpUseSSL?: boolean;
  smtpUseTLS?: number;
  maxNumberOfLabels?: number;
  pListShortageRule?: number;
  consignmentSWURL?: string;
  consignmentSWExternalID1?: string;
  consignmentSWExternalID2?: string;
  stdSQLFilter?: number;
  stdSQLGroup?: number;
  stdSQLOrder?: number;
  stdRuleGroupID?: number;
  orderStrainFactor?: number;
  picklistStrainFactor?: number;
  labelExportFolder?: string;
  packageSlipPrinterName?: string;
  orderExportFolder?: string;
  grExportFolder?: string;
  stockTransExportFolder?: string;
  pictureFolder?: string;
  printSetupFromDMYesNo: boolean;
  shippingLabelsLayoutNo?: number;
  shippingLabelsYesNo?: boolean;
  packageSlipERPYesNo?: boolean;
  shippingLabelsERPYesNo: boolean;
  consignmentNoteERPYesNo: boolean;
  shippingLabelsPrinterName?: string;
  consignmentNotePrinterName?: string;
  packageSlipFormNo?: number;
  shippingLabelsFormNo?: number;
  consignmentNoteFormNo?: number;
  printerID?: string;
  warehouseMethod?: number;
  itemUnitsYesNo?: boolean;
  packageLabelExportFolder?: string;
  packagePrinterID?: string;
  packageLabelsLayoutNo?: number;
  consignmentSoftwareID?: number;
  getExternalShipmentID?: boolean;
  stdDistrRuleGroupID?: number;
  distributionYesNo?: boolean;
  smtpServer?: string;
  smtpPort?: number;
  smtpUser?: string;
  smtpPwd?: string;
  smtpSenderAddress?: string;
  smtpSenderName?: string;
  distrShowPickStatusInApp?: boolean;
  ordersTxtVisibleOnPList?: boolean;
  companyAddress1?: string;
  companyAddress2?: string;
  companyZipCode?: string;
  companyCity?: string;
  companyCountry?: string;
  reportFolder?: string;
  packageSlipNumOfCopies?: number;
  packingSQLGroupID?: number;
  packingSQLVisibleGroupID?: number;
  packingSQLVisibleDetailGroupID?: number;
  cloudStorageAppKey?: string;
  cloudStorageAppSecret?: string;
  cloudStorageType?: number;
  putAwayMaxOrderLines?: number;
  replenishmentMaxOrderLines?: number;
  exportShipmentFiles?: boolean;
  createPutAwayOnInCompleteItems?: boolean;
  stdPutAwayRuleGroupID?: number;
  stdReplenishmentRuleGroupID?: number;
  stdOptimalizationRuleGroupID?: number;
  keepPOrdersHistory?: boolean;
  useExternalEvents?: boolean;
  smsSenderAddress?: string;
  splitSharedBinsYesNo?: boolean;
  systemLogLevel?: number;
  exportShipmentLineTo?: number;
  pListSumUpOnItem?: boolean;
  stcReservationOrderByQty?: boolean;
  archiveNumOfDays?: number;
  exportToExternalReporting?: boolean;
  extReportingPathComplete?: string;
  extReportingPathChanges?: string;
  extReportingLastChangesExport?: string;
  extReportingCompleteExportTime?: string;
  extReportingLastCompleteExport?: string;
  extReportingLink?: string;
  appLoglimit?: number;
  externalShipmentVolumeFactor?: number;
  externalShipmentWeightFactor?: number;
  externalShipmentDimensionFactor?: number;
  stcReplenishmentOrderByQty?: boolean;
  stdPutAwayListModeRuleGroupID?: number;
  replenishmentRuleGroupIDFromApp?: number;
  updateStockOnPListToPacking?: boolean;
  updateStockOnPListToPackingBinCode?: string;
  maxNumOfPackages?: number;
  updatedBy?: string;
  lastUpdate?: string;
  createdBy?: string;
  created?: string;
}

export type UpdateSystemSetupRequest = Omit<SystemSetupDto, 'id' | 'companyNo' | 'updatedBy' | 'lastUpdate' | 'createdBy' | 'created'>;

export const systemSetupService = {
  /**
   * Get current tenant's system setup
   */
  async getCurrent(): Promise<SystemSetupDto> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/system-setup/current`
    );
    if (!response.ok) {
      throw new Error(`Failed to get system setup: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Update current tenant's system setup
   */
  async updateCurrent(data: Partial<UpdateSystemSetupRequest>): Promise<SystemSetupDto> {
    const response = await AuthService.authenticatedFetch(
      `${API_BASE_URL}/system-setup/current`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Failed to update system setup'));
    }
    return response.json();
  }
};
